import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getShowcaseData } from '../services/gig.service';
import { ps1, ps2, ps3, ps4, ps5 } from '../assets/assets';

const ProjectShowcase = () => {
  const [isMobile, setIsMobile] = useState(false);

  // Fetch showcase data
  const { data: showcaseData, isLoading } = useQuery({
    queryKey: ['showcase'],
    queryFn: getShowcaseData,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  });

  const showcase = showcaseData?.data || {};

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const projects = [
    {
      id: 1,
      title: "Today's Project Handpicked For You",
      image: showcase.handpicked?.[0]?.cover || ps1,
      link: showcase.handpicked?.[0]?.slug ? `/gigs/${showcase.handpicked[0].slug}` : "/category/all-services",
      className: "col-span-1 row-span-2 md:col-span-1 md:row-span-2"
    },
    {
      id: 2,
      title: "Explore",
      image: ps2,
      link: "/category/all-services",
      className: "col-span-1 row-span-1 md:col-span-1 md:row-span-1"
    },
    {
      id: 3,
      title: showcase.bestCategory ? `Best ${showcase.bestCategory.name}` : "Best Selling",
      image: showcase.bestCategory?.topGig?.cover || ps3,
      link: showcase.bestCategory?.slug ? `/category/${showcase.bestCategory.slug}` : "/category/all-services",
      className: "col-span-1 row-span-1 md:col-span-1 md:row-span-1"
    },
    {
      id: 4,
      title: "Best Selling Gigs",
      image: showcase.bestSelling?.[0]?.cover || ps4,
      link: "/category/all-services?sort=sales",
      className: "col-span-1 row-span-1 md:col-span-1 md:row-span-1"
    },
    {
      id: 5,
      title: "Explore Our Services",
      image: ps5,
      link: "/services",
      className: "col-span-1 row-span-1 md:col-span-1 md:row-span-1"
    }
  ];

  // Function to handle navigation
  const handleNavigation = (link) => {
    window.location.href = link;
  };

  // Desktop Grid Layout
  const DesktopView = () => (
    <div className="grid grid-cols-3 grid-rows-2 gap-4 h-[600px]">
      {/* Left large column spanning 2 rows */}
      <div 
        className="col-span-1 row-span-2 relative cursor-pointer transform hover:scale-105 transition-transform duration-300 hover:shadow-xl"
        onClick={() => handleNavigation(projects[0].link)}
      >
        <img 
          src={projects[0].image} 
          alt={projects[0].title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center p-8">
          <h2 className="text-white text-4xl font-bold text-center">
            {projects[0].title}
          </h2>
        </div>
      </div>

      {/* Top right section - 2 columns */}
      <div 
        className="col-span-1 row-span-1 relative cursor-pointer transform hover:scale-105 transition-transform duration-300 hover:shadow-xl"
        onClick={() => handleNavigation(projects[1].link)}
      >
        <img 
          src={projects[1].image} 
          alt={projects[1].title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-30 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
          <h2 className="text-white text-3xl font-bold">{projects[1].title}</h2>
        </div>
      </div>
      
      <div 
        className="col-span-1 row-span-1 relative cursor-pointer transform hover:scale-105 transition-transform duration-300 hover:shadow-xl"
        onClick={() => handleNavigation(projects[2].link)}
      >
        <img 
          src={projects[2].image} 
          alt={projects[2].title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-30 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
          <h2 className="text-white text-3xl font-bold">{projects[2].title}</h2>
        </div>
      </div>

      {/* Bottom right section - 2 columns */}
      <div 
        className="col-span-1 row-span-1 relative cursor-pointer transform hover:scale-105 transition-transform duration-300 hover:shadow-xl"
        onClick={() => handleNavigation(projects[3].link)}
      >
        <img 
          src={projects[3].image} 
          alt={projects[3].title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-30 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
          <h2 className="text-white text-3xl font-bold text-center">{projects[3].title}</h2>
        </div>
      </div>
      
      <div 
        className="col-span-1 row-span-1 relative cursor-pointer transform hover:scale-105 transition-transform duration-300 hover:shadow-xl"
        onClick={() => handleNavigation(projects[4].link)}
      >
        <img 
          src={projects[4].image} 
          alt={projects[4].title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-30 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
          <h2 className="text-white text-3xl font-bold text-center">{projects[4].title}</h2>
        </div>
      </div>
    </div>
  );

  // Mobile Grid Layout
  const MobileView = () => (
    <div className="grid grid-cols-2 grid-rows-3 gap-2 max-w-md mx-auto">
      {/* Large top-left image */}
      <div 
        className="col-span-1 row-span-2 relative cursor-pointer transform hover:scale-105 transition-transform duration-300"
        onClick={() => handleNavigation(projects[0].link)}
      >
        <img 
          src={projects[0].image} 
          alt={projects[0].title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center p-4">
          <h2 className="text-white text-lg font-bold text-center">
            {projects[0].title}
          </h2>
        </div>
      </div>

      {/* Top right column */}
      <div 
        className="col-span-1 row-span-1 relative cursor-pointer transform hover:scale-105 transition-transform duration-300"
        onClick={() => handleNavigation(projects[1].link)}
      >
        <img 
          src={projects[1].image} 
          alt={projects[1].title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-30 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
          <h2 className="text-white text-base font-bold">{projects[1].title}</h2>
        </div>
      </div>
      
      <div 
        className="col-span-1 row-span-1 relative cursor-pointer transform hover:scale-105 transition-transform duration-300"
        onClick={() => handleNavigation(projects[2].link)}
      >
        <img 
          src={projects[2].image} 
          alt={projects[2].title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-30 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center p-2">
          <h2 className="text-white text-base font-bold text-center">{projects[2].title}</h2>
        </div>
      </div>

      {/* Bottom row */}
      <div 
        className="col-span-1 row-span-1 relative cursor-pointer transform hover:scale-105 transition-transform duration-300"
        onClick={() => handleNavigation(projects[3].link)}
      >
        <img 
          src={projects[3].image} 
          alt={projects[3].title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-30 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center p-2">
          <h2 className="text-white text-base font-bold text-center">{projects[3].title}</h2>
        </div>
      </div>
      
      <div 
        className="col-span-1 row-span-1 relative cursor-pointer transform hover:scale-105 transition-transform duration-300"
        onClick={() => handleNavigation(projects[4].link)}
      >
        <img 
          src={projects[4].image} 
          alt={projects[4].title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-30 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center p-2">
          <h2 className="text-white text-base font-bold text-center">{projects[4].title}</h2>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto p-4">
      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 h-[600px]">
          <div className="col-span-1 row-span-2 bg-gray-200 animate-pulse rounded-lg"></div>
          <div className="col-span-1 bg-gray-100 animate-pulse rounded-lg"></div>
          <div className="col-span-1 bg-gray-100 animate-pulse rounded-lg"></div>
          <div className="col-span-1 bg-gray-100 animate-pulse rounded-lg"></div>
          <div className="col-span-1 bg-gray-100 animate-pulse rounded-lg"></div>
        </div>
      ) : isMobile ? <MobileView /> : <DesktopView />}
    </div>
  );
};

export default ProjectShowcase;